<template>
  <base-input :label="label">
    <Autocomplete
      style="width: 100%"
      :value="value"
      placeholder="Start typing an address"
      :fetch-suggestions="search"
      :debounce="300"
      :trigger-on-focus="false"
      popper-class="address-search-popper"
      value-key="value"
      @input="emit('input', $event)"
      @select="onSelect"
    >
      <template #default="{ item }">
        <span class="address-search__line1">{{ item.line1 }}</span>
        <span class="address-search__line2">{{ item.line2 }}</span>
      </template>
    </Autocomplete>
  </base-input>
</template>

<script setup lang="ts">
// Address line 1 with address suggestions (OTC-2926), Amazon Location Places v2
// on the otc-console-maps key. Suggestions are display only (AWS service terms
// 82.4(a)); picking one runs a Geocode with IntendedUse 'Storage' and emits its
// result as `pick`. Japan gets no lookups (82.5(a)).
import axios from 'axios'
// Imported by source name so babel-plugin-component resolves the matching
// CSS as element-ui/lib/theme-chalk/autocomplete.css. Aliasing the import
// (e.g. `Autocomplete as ElAutocomplete`) makes the plugin look for
// el-autocomplete.css, which doesn't exist. Element UI's Autocomplete is
// not globally registered (see src/plugins/globalComponents.js); the
// template tag below uses <Autocomplete> so script-setup binds it.
import { Autocomplete } from 'element-ui'
import { MAPS_API_KEY, MAPS_PLACES_URL } from '@/api-config'
import { suggestionFromAutocomplete, addressFromGeocode, type AddressSuggestion } from '@/util/addressSearch'

const props = defineProps({
  value: { type: String, default: '' },
  label: { type: String, default: 'Address line 1' },
  countryCode: { type: String, default: '' },
})

const emit = defineEmits(['input', 'pick'])

const JAPAN = 'JP'
let typedText = ''

function countryFilter(code: string) {
  return code ? { Filter: { IncludeCountries: [code] } } : {}
}

async function search(query: string, cb: (items: AddressSuggestion[]) => void) {
  typedText = query
  const text = (query || '').trim()
  if (text.length < 3 || props.countryCode === JAPAN) return cb([])
  try {
    const response = await axios.post(`${MAPS_PLACES_URL}/autocomplete?key=${MAPS_API_KEY}`, {
      QueryText: text.slice(0, 200),
      MaxResults: 5,
      ...countryFilter(props.countryCode),
    })
    const items = (response.data?.ResultItems || [])
      .map(suggestionFromAutocomplete)
      .filter((s: AddressSuggestion) => s.countryCode2 !== JAPAN)
    cb(items)
  } catch (err) {
    cb([])
  }
}

async function onSelect(item: AddressSuggestion) {
  const typed = typedText
  emit('input', item.line1)
  try {
    // Autocomplete returns only the label, so the country filter comes from
    // the group's selected country when the suggestion has none.
    const response = await axios.post(`${MAPS_PLACES_URL}/geocode?key=${MAPS_API_KEY}`, {
      QueryText: item.label.slice(0, 200),
      MaxResults: 1,
      IntendedUse: 'Storage',
      ...countryFilter(item.countryCode2 || props.countryCode),
    })
    const first = response.data?.ResultItems?.[0]
    const address = first ? addressFromGeocode(first) : null
    if (address && address.countryCode2 !== JAPAN) {
      emit('input', address.line1)
      emit('pick', address)
      return
    }
  } catch (err) {
    // Falls through to restore the typed text.
  }
  emit('input', typed)
}
</script>
